import React from 'react';
import { Clock, Mail, Phone, Building2, Trash2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import TrackingPanel from './TrackingPanel';

const yesNo = (v) => {
  if (v === 'yes') return 'כן';
  if (v === 'no') return 'לא';
  return null;
};

const formatHebDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

/**
 * Single lead card rendered inside the Admin dashboard list.
 * Pure presentational — all side-effects live in the parent.
 */
const LeadCard = ({ lead, srcMeta, isExpanded, onToggleExpand, onStatusChange, onDelete }) => {
  const yesNoFlags = [
    { label: 'מכירות', val: yesNo(lead.salesTeam) },
    { label: 'קמפיינים', val: yesNo(lead.activeCampaigns) },
    { label: 'ספקים', val: yesNo(lead.workingWithVendors) },
  ].filter((f) => f.val !== null);

  return (
    <article
      className="bg-cream border border-navy/10 p-6 lift-card"
      data-testid={`admin-lead-${lead.id}`}
    >
      {/* Source badge — top strip */}
      <div className="flex flex-wrap items-center justify-between gap-2 pb-4 mb-4 border-b border-navy/10">
        <Badge className={`${srcMeta.color} hover:opacity-90 text-xs px-3 py-1 font-medium`}>
          {srcMeta.label}
        </Badge>
        <p className="text-xs text-navy/50 flex items-center gap-1.5">
          <Clock className="w-3 h-3" aria-hidden="true" />
          {formatHebDate(lead.createdAt)}
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-4 items-start">
        {/* Identity */}
        <div className="lg:col-span-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-navy text-cream flex items-center justify-center font-serif text-lg flex-shrink-0">
              {(lead.fullName || '?').charAt(0)}
            </div>
            <div>
              <p className="font-serif text-xl text-navy">{lead.fullName || 'ללא שם'}</p>
              {lead.role && <p className="text-sm text-navy/60">{lead.role}</p>}
              {lead.company && (
                <p className="text-sm text-navy/70 flex items-center gap-1 mt-1">
                  <Building2 className="w-3 h-3" aria-hidden="true" /> {lead.company}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="lg:col-span-3 space-y-1.5 text-sm">
          {lead.email && (
            <a
              href={`mailto:${lead.email}`}
              className="flex items-center gap-2 text-navy/80 hover:text-bronze break-all"
            >
              <Mail className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" /> {lead.email}
            </a>
          )}
          {lead.phone && (
            <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-navy/80 hover:text-bronze">
              <Phone className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" /> {lead.phone}
            </a>
          )}
          {yesNoFlags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-navy/10">
              {yesNoFlags.map((f) => (
                <span
                  key={f.label}
                  className={`text-[11px] px-2 py-0.5 ${
                    f.val === 'כן' ? 'bg-bronze/20 text-bronze' : 'bg-navy/10 text-navy/60'
                  }`}
                >
                  {f.label}: {f.val}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="lg:col-span-3 text-sm text-navy/70 space-y-2">
          {lead.challenge && (
            <div>
              <p className="eyebrow text-navy/40 text-[10px] mb-1">אתגר</p>
              <p className="line-clamp-4">{lead.challenge}</p>
            </div>
          )}
          {lead.selling && (
            <div>
              <p className="eyebrow text-navy/40 text-[10px] mb-1">מוכרים</p>
              <p className="line-clamp-2">{lead.selling}</p>
            </div>
          )}
          {lead.improve && (
            <Badge className="bg-bronze/15 text-bronze hover:bg-bronze/25 text-[10px]">
              רוצים לשפר: {lead.improve}
            </Badge>
          )}
        </div>

        {/* Actions */}
        <div className="lg:col-span-2 flex flex-col gap-2 lg:items-end">
          <Select value={lead.status} onValueChange={(v) => onStatusChange(lead.id, v)}>
            <SelectTrigger className="w-32 h-8 text-xs" aria-label={`שינוי סטטוס ל-${lead.fullName}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">חדש</SelectItem>
              <SelectItem value="contacted">בטיפול</SelectItem>
              <SelectItem value="closed">נסגר</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={() => onDelete(lead.id)}
            className="text-destructive text-xs flex items-center gap-1 hover:underline"
          >
            <Trash2 className="w-3 h-3" aria-hidden="true" /> מחק
          </button>
        </div>
      </div>

      {/* Marketing & visitor attribution */}
      <TrackingPanel lead={lead} isOpen={isExpanded} onToggle={() => onToggleExpand(lead.id)} />
    </article>
  );
};

export default LeadCard;
